import React from 'react';
import usePageMeta from '../hooks/usePageMeta';

const PrivacyPolicy: React.FC = () => {
  usePageMeta({
    title: 'Privacy Policy — BoostHiring',
    description:
      'BoostHiring Privacy Policy. Learn how we collect, use, and protect your data on the BoostHiring recruitment platform.',
    canonical: 'https://boosthiring.ca/privacy',
  });

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-6 py-16 max-w-4xl">
        <header className="mb-12 border-b border-border-muted pb-8 flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold text-primary mb-3">Privacy Policy</h1>
            <p className="text-subtle text-lg">Last Updated: April 20th, 2026</p>
          </div>
          <button
            type="button"
            onClick={() => window.print()} // eslint-disable-line no-undef
            className="font-medium text-primary border border-border-muted rounded-lg px-4 py-2 hover:bg-bg-muted transition-colors print:hidden"
          >
            <span role="img" aria-label="Print">
              🖨️
            </span>{' '}
            Print
          </button>
        </header>

        <div className="prose max-w-none text-slate-700 leading-relaxed space-y-10">
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">1. Overview</h2>
            <p>BoostHiring respects your privacy and is committed to protecting your data.</p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">2. What We Collect</h2>
            <p className="mb-3">We may collect:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>account information (name, email, company)</li>
              <li>usage data (platform activity)</li>
              <li>communication data</li>
            </ul>
            <p className="font-semibold">We do NOT store candidate resumes long-term.</p>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">3. Candidate Data</h2>
            <p className="mb-3">Candidate data is submitted by Recruiters.</p>
            <p className="mb-3">Recruiters are responsible for:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>obtaining consent</li>
              <li>ensuring legal compliance</li>
            </ul>
            <p className="mb-3">BoostHiring:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>does not verify candidate consent</li>
              <li>does not control how data is collected</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">4. How We Use Data</h2>
            <p className="mb-3">We use data to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>operate the platform</li>
              <li>improve services</li>
              <li>support users</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">5. Data Sharing</h2>
            <p className="mb-3">We do not sell data.</p>
            <p className="mb-3">We may share data:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>between Employers and Recruiters (strictly within platform use)</li>
              <li>if required by law</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">6. Data Storage</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Data may be stored in Canada or the USA</li>
              <li>We use secure infrastructure (e.g., AWS, Google Cloud or Microsoft)</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">7. Data Retention</h2>
            <p className="mb-3">Candidate data is deleted after:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>job closure</li>
              <li>rejection</li>
              <li>reasonable processing period</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">8. Your Rights</h2>
            <p className="mb-3">You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>
                access your data once the position remains open and you have an active account
              </li>
              <li>request corrections</li>
              <li>request deletion</li>
            </ul>
            <p>
              Contact:{' '}
              <a
                href="mailto:admin@boosthiring.ca"
                className="text-tertiary hover:underline font-medium"
              >
                admin@boosthiring.ca
              </a>
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">9. Security</h2>
            <p>We implement reasonable safeguards but cannot guarantee absolute security.</p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold text-primary mb-4">10. Changes</h2>
            <p>We may update this policy at any time.</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
